import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AdminUserList from './AdminUserList.jsx';
import AdminRepository from '../../services/AdminRepository.js';
import ClinicStatisticsLogsPanel from './ClinicStatisticsLogsPanel.jsx';
import { buildRoleStatEntries } from '../../utils/roleStatsUtils.js';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { formatStripeWarningMessage } from '../../utils/stripeWarningUtils.js';
import './AdminClinicDetail.css';

const toTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value?._seconds === 'number') return value._seconds * 1000;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatDate = (value) => {
  const ms = toTimestampMs(value);
  if (!ms) return '未設定';
  return new Date(ms).toLocaleDateString('ja-JP');
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '未設定';
  return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
};

const LICENSE_STATUS_LABELS = {
  active: '本利用',
  trial: '試用',
  past_due: '支払い遅延',
  canceled: 'キャンセル',
  expired: '期限切れ',
};

const END_TYPE_LABELS = {
  trial: '試用期限',
  billingDeferral: '支払猶予期限',
  canceledUsable: '利用可能期限',
};

const getLicenseStatusClass = (status) => {
  if (status === 'active') return 'success';
  if (status === 'trial') return 'info';
  if (status === 'past_due') return 'warning';
  if (status === 'canceled' || status === 'expired') return 'danger';
  return 'muted';
};

const getContractEndInfo = (contract) => {
  if (!contract) return { label: '期限', value: '未設定' };
  if (contract.status === 'trial' && contract.trialEndAt) {
    return { label: END_TYPE_LABELS.trial, value: formatDate(contract.trialEndAt) };
  }
  if (contract.status === 'active' && contract.billingDeferralUntil) {
    return { label: END_TYPE_LABELS.billingDeferral, value: formatDate(contract.billingDeferralUntil) };
  }
  if (contract.status === 'canceled' && contract.usableUntil) {
    return { label: END_TYPE_LABELS.canceledUsable, value: formatDate(contract.usableUntil) };
  }
  return { label: '期限', value: '未設定' };
};

const formatPaymentMethod = (method) => {
  if (!method) return '未設定';
  if (method === 'credit') return 'カード決済';
  return method;
};

const getPaymentMethods = (paymentSummary) => (
  paymentSummary?.paymentMethods || paymentSummary?.cards || []
);

const getPaymentMethodCount = (paymentSummary) => {
  if (!paymentSummary) return 0;
  if (typeof paymentSummary.paymentMethodCount === 'number') return paymentSummary.paymentMethodCount;
  return paymentSummary.cardCount || 0;
};

const formatPaymentMethodTitle = (method) => {
  if (!method) return '支払い方法';
  if (method.type === 'card') {
    return `${String(method.brand || 'card').toUpperCase()} ****${method.last4 || '----'}`;
  }
  return method.displayName || method.type || '支払い方法';
};

const formatPaymentMethodSubText = (method) => {
  if (!method) return '';
  if (method.type === 'card') {
    return `有効期限 ${method.expMonth || '--'}/${method.expYear || '----'}`;
  }
  return 'Stripeに登録済み';
};

const formatYenAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
};

const INVOICE_STATUS_LABELS = {
  paid: '支払済',
  open: '未払い',
  draft: '下書き',
  void: '無効',
  uncollectible: '回収不能',
};

const formatInvoiceStatus = (status) => INVOICE_STATUS_LABELS[status] || status || '不明';

const formatUnixDateTime = (unix) => {
  if (!unix && unix !== 0) return '—';
  return new Date(Number(unix) * 1000).toLocaleString('ja-JP');
};

/**
 * クリニック単位の管理画面（利用状況 + ユーザー管理）
 */
const AdminClinicDetail = ({
  clinic,
  clinicStats,
  users,
  clinics,
  licenseLimit,
  onBack,
  onUpdate,
  onEditClinic,
  user = null,
}) => {
  const { showAlert, showConfirm } = useAdminDialog();
  const [roleList, setRoleList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [stripeActionKey, setStripeActionKey] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState('');
  const effectiveClinic = detail?.clinic || clinic;
  // 詳細の code/name を先頭に載せ、ユーザー作成完了モーダルのコピー欠けを防ぐ
  const clinicsForUserList = useMemo(() => {
    const current = effectiveClinic || clinic;
    if (!current?.id) return clinics || [];
    const others = (clinics || []).filter((c) => c.id !== current.id);
    return [{ ...current }, ...others];
  }, [effectiveClinic, clinic, clinics]);
  const stats = clinicStats[effectiveClinic.id] || clinicStats[clinic.id] || {};
  const userStats = detail?.userStats || stats.userStats || { total: 0, byRole: {} };
  const licenseStats = stats.licenseStats || {
    used: 0,
    available: licenseLimit,
    licenses: [],
    hasExpiredLicense: false,
  };
  const invStats = stats.invitation || { points: 0, invitedCount: 0, code: null };
  const roleStatEntries = buildRoleStatEntries(userStats.byRole, roleList);
  const licenseContracts = detail?.licenseContracts || [];
  const paymentSummary = detail?.paymentSummary || null;
  const clinicUpdatedAtMs = toTimestampMs(clinic.updatedAt);

  useEffect(() => {
    let cancelled = false;
    AdminRepository.getInstance().getRoleList().then((list) => {
      if (!cancelled) setRoleList(Array.isArray(list) ? list : []);
    }).catch(() => {
      if (!cancelled) setRoleList([]);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    AdminRepository.getInstance().getClinicDetail(clinic.id)
      .then((data) => {
        if (!cancelled) setDetail(data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(err.message || 'クリニック詳細の取得に失敗しました');
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [clinic.id, clinicUpdatedAtMs]);

  useEffect(() => {
    let cancelled = false;
    setPaymentHistoryLoading(true);
    setPaymentHistoryError('');
    AdminRepository.getInstance().getClinicPaymentHistory(clinic.id, { limit: 20 })
      .then((data) => {
        if (!cancelled) setPaymentHistory(data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setPaymentHistory(null);
          setPaymentHistoryError(err.message || '決済履歴の取得に失敗しました');
        }
      })
      .finally(() => {
        if (!cancelled) setPaymentHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [clinic.id, clinicUpdatedAtMs]);

  const refreshPaymentHistory = useCallback(async () => {
    setPaymentHistoryLoading(true);
    setPaymentHistoryError('');
    try {
      const data = await AdminRepository.getInstance().getClinicPaymentHistory(clinic.id, { limit: 20 });
      setPaymentHistory(data || null);
    } catch (err) {
      setPaymentHistory(null);
      setPaymentHistoryError(err.message || '決済履歴の取得に失敗しました');
    } finally {
      setPaymentHistoryLoading(false);
    }
  }, [clinic.id]);

  const refreshClinicDetail = useCallback(async () => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const data = await AdminRepository.getInstance().getClinicDetail(clinic.id);
      setDetail(data || null);
      onUpdate?.();
      await refreshPaymentHistory();
    } catch (err) {
      setDetailError(err.message || 'クリニック詳細の再取得に失敗しました');
    } finally {
      setDetailLoading(false);
    }
  }, [clinic.id, onUpdate, refreshPaymentHistory]);

  const formatStripeWarningsForAlert = formatStripeWarningMessage;

  const handleClearStripeLink = async (contract) => {
    if (!contract?.licenseName) return;
    const confirmed = await showConfirm({
      title: 'Stripe 連携をクリア',
      message: `「${contract.displayName || contract.licenseName}」の Stripe サブスク ID を Firestore からクリアします。Stripe 上のサブスク自体は変更しません。続行しますか？`,
      confirmLabel: 'クリアする',
      danger: true,
    });
    if (!confirmed) return;

    const actionKey = `${contract.licenseName}:clear`;
    setStripeActionKey(actionKey);
    try {
      const result = await AdminRepository.getInstance().clearClinicLicenseStripeLink(
        clinic.id,
        contract.licenseName,
        { status: contract.status || 'active' },
      );
      const warningText = formatStripeWarningsForAlert(result?.stripeWarnings);
      await showAlert({
        title: '完了',
        variant: 'success',
        message: warningText || 'Stripe 連携 ID をクリアしました。',
      });
      await refreshClinicDetail();
    } catch (err) {
      await showAlert({
        title: 'エラー',
        variant: 'error',
        message: err.message || 'Stripe 連携のクリアに失敗しました。',
      });
    } finally {
      setStripeActionKey(null);
    }
  };

  const handleRepairStripeLink = async (contract) => {
    if (!contract?.licenseName) return;
    const confirmed = await showConfirm({
      title: 'Stripe 再連携',
      message: `「${contract.displayName || contract.licenseName}」の Stripe サブスクリプションを連携します。\n既に同一ライセンス用の有効なサブスクが Stripe 上にある場合は新規作成せず再利用します。`,
      confirmLabel: '再連携する',
    });
    if (!confirmed) return;

    const actionKey = `${contract.licenseName}:repair`;
    setStripeActionKey(actionKey);
    try {
      const result = await AdminRepository.getInstance().repairClinicLicenseStripeLink(
        clinic.id,
        contract.licenseName,
        { status: contract.status || 'active' },
      );
      const warningText = formatStripeWarningsForAlert(result?.stripeWarnings);
      await showAlert({
        title: '完了',
        variant: 'success',
        message: warningText || 'Stripe 再連携処理が完了しました。',
      });
      await refreshClinicDetail();
    } catch (err) {
      await showAlert({
        title: 'エラー',
        variant: 'error',
        message: err.message || 'Stripe 再連携に失敗しました。',
      });
    } finally {
      setStripeActionKey(null);
    }
  };

  return (
    <div className="clinic-detail">
      <button type="button" className="clinic-detail-back" onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        クリニック一覧に戻る
      </button>

      <div className="clinic-detail-title-row">
        <div>
          <div className="clinic-detail-badges">
            <span className="badge info">{licenseStats.used} ライセンス</span>
            {effectiveClinic.isDemo && <span className="badge info">デモ</span>}
            {licenseStats.hasExpiredLicense && (
              <span className="badge danger">ライセンス切れあり</span>
            )}
          </div>
          <h1>{effectiveClinic.name}</h1>
          <div className="clinic-detail-meta">
            <span className="meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              ID: {effectiveClinic.id}
            </span>
            <span className="meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 7h16" />
                <path d="M9 7V5a3 3 0 0 1 6 0v2" />
                <rect x="4" y="7" width="16" height="14" rx="2" />
              </svg>
              コード: <span className="mono">{effectiveClinic.code || '未設定'}</span>
            </span>
            <span className="meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              登録日: {formatDate(effectiveClinic.createdAt)}
            </span>
            {effectiveClinic.email && (
              <span className="meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {effectiveClinic.email}
              </span>
            )}
          </div>
        </div>
        {effectiveClinic?.isDeleted !== true && (
          <div className="clinic-detail-actions">
            <button type="button" className="btn btn-primary" onClick={() => onEditClinic(effectiveClinic)}>
              クリニックを編集
            </button>
          </div>
        )}
      </div>

      {detailError && (
        <div className="clinic-detail-alert danger">
          {detailError}
        </div>
      )}

      <div className="clinic-detail-stats">
        <div className="clinic-detail-stat-card">
          <div className="stat-label">ユーザー</div>
          <div className="stat-value">
            {effectiveClinic.userLimit == null ? (
              <>
                {userStats.total}/<span style={{ fontSize: '1rem' }}>上限なし</span>
              </>
            ) : (
              <span className={userStats.total >= effectiveClinic.userLimit ? 'text-danger' : ''}>
                {userStats.total}/{effectiveClinic.userLimit}名
              </span>
            )}
          </div>
          <div className="stat-breakdown">
            {roleStatEntries.map((entry) => (
              <span key={entry.value}>{entry.label}: {entry.count}</span>
            ))}
            <span>上限: {effectiveClinic.userLimit != null ? `${effectiveClinic.userLimit}名` : 'なし'}</span>
          </div>
        </div>

        <div className="clinic-detail-stat-card">
          <div className="stat-label">ライセンス</div>
          <div className="stat-value">{licenseStats.used}/{licenseLimit}</div>
          <div className="stat-breakdown">
            <span>使用中: {licenseStats.used}</span>
            <span>空き: {licenseStats.available}</span>
          </div>
        </div>

        <div className="clinic-detail-stat-card">
          <div className="stat-label">招待</div>
          <div
            className="stat-value"
            style={{ color: invStats.points > 0 ? 'var(--color-accent-primary)' : undefined }}
          >
            {invStats.points}
            <span style={{ fontSize: '0.875rem', fontWeight: 400 }}>pt</span>
          </div>
          <div className="stat-breakdown">
            <span>招待数: {invStats.invitedCount}</span>
            {invStats.code && <span>コード: {invStats.code}</span>}
          </div>
        </div>

        <div className="clinic-detail-stat-card">
          <div className="stat-label">決済登録</div>
          <div className={`stat-value payment-status ${paymentSummary?.isRegistered ? 'is-ok' : 'is-ng'}`}>
            {detailLoading && !paymentSummary ? '確認中' : paymentSummary?.isRegistered ? '完了' : '未登録'}
          </div>
          <div className="stat-breakdown">
            <span>支払い方法: {paymentSummary ? `${getPaymentMethodCount(paymentSummary)}件` : '確認中'}</span>
            <span>既定: {paymentSummary?.defaultPaymentMethodLabel || '未設定'}</span>
          </div>
        </div>
      </div>

      <div className="clinic-detail-grid">
        <section className="clinic-detail-panel clinic-detail-panel--wide">
          <div className="clinic-detail-panel-header">
            <div>
              <h2>契約ライセンス</h2>
              <p>契約中・試用中・解約済みを含むライセンス契約の詳細</p>
            </div>
            <span className="badge info">{licenseContracts.length || licenseStats.licenses?.length || 0} 件</span>
          </div>

          {detailLoading && licenseContracts.length === 0 ? (
            <div className="clinic-detail-empty">契約情報を読み込んでいます...</div>
          ) : licenseContracts.length === 0 ? (
            <div className="clinic-detail-empty">契約ライセンスはありません。</div>
          ) : (
            <div className="clinic-license-table-wrap">
              <table className="clinic-license-table">
                <thead>
                  <tr>
                    <th>ライセンス</th>
                    <th>状態</th>
                    <th>開始日</th>
                    <th>期限</th>
                    <th>最終支払</th>
                    <th>Stripe</th>
                  </tr>
                </thead>
                <tbody>
                  {licenseContracts.map((contract) => {
                    const endInfo = getContractEndInfo(contract);
                    return (
                      <tr key={contract.id || contract.licenseName}>
                        <td data-label="ライセンス">
                          <div className="license-name-cell">
                            <strong>{contract.displayName || contract.licenseName || '名称未設定'}</strong>
                            <span>{contract.licenseName || contract.id}</span>
                            {contract.slotKey && <span>スロット: {contract.slotKey}</span>}
                            {contract.monthlyPrice != null && (
                              <em>{contract.isFree ? '無料' : `月額 ${formatCurrency(contract.monthlyPrice)}（税別）`}</em>
                            )}
                            {contract.contractMissing && <em>契約詳細ドキュメント未作成</em>}
                            {contract.detachedFromLicenseSlot && <em>ライセンススロット未紐付け</em>}
                          </div>
                        </td>
                        <td data-label="状態">
                          <span className={`status-pill ${getLicenseStatusClass(contract.status)}`}>
                            {LICENSE_STATUS_LABELS[contract.status] || contract.status || '不明'}
                          </span>
                        </td>
                        <td data-label="開始日">{formatDate(contract.trialStartAt || contract.createdAt || contract.billingAnchor)}</td>
                        <td data-label="期限">
                          <div className="license-date-cell">
                            <span>{endInfo.value}</span>
                            <small>{endInfo.label}</small>
                          </div>
                        </td>
                        <td data-label="最終支払">{formatDate(contract.lastPaidAt)}</td>
                        <td data-label="Stripe">
                          <div className="stripe-link-cell">
                            {contract.stripeSubscriptionId ? (
                              <code className="clinic-detail-code">{contract.stripeSubscriptionId}</code>
                            ) : (
                              <span className="text-muted">未連携</span>
                            )}
                            {contract.stripeSubscriptionIdAtStale && (
                              <span className="stripe-link-history" title="無効化された過去の連携 ID">
                                退避: <code className="clinic-detail-code">{contract.stripeSubscriptionIdAtStale}</code>
                              </span>
                            )}
                            {contract.stripeSubscriptionIdAtRevive && (
                              <span className="stripe-link-history" title="解約復活前の連携 ID">
                                復活前: <code className="clinic-detail-code">{contract.stripeSubscriptionIdAtRevive}</code>
                              </span>
                            )}
                            {contract.licenseName && contract.status !== 'canceled' && (
                              <div className="stripe-link-actions">
                                {contract.stripeSubscriptionId && (
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    disabled={stripeActionKey === `${contract.licenseName}:clear`}
                                    onClick={() => handleClearStripeLink(contract)}
                                  >
                                    {stripeActionKey === `${contract.licenseName}:clear` ? '処理中…' : '連携クリア'}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  disabled={stripeActionKey === `${contract.licenseName}:repair`}
                                  onClick={() => handleRepairStripeLink(contract)}
                                >
                                  {stripeActionKey === `${contract.licenseName}:repair` ? '処理中…' : '再連携'}
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="clinic-detail-panel">
          <div className="clinic-detail-panel-header">
            <div>
              <h2>決済登録状況</h2>
              <p>Stripe Customer と登録済み支払い方法の状態</p>
            </div>
          </div>

          {detailLoading && !paymentSummary ? (
            <div className="clinic-detail-empty">決済情報を確認しています...</div>
          ) : (
            <div className="payment-summary">
              <div className={`payment-summary-status ${paymentSummary?.isRegistered ? 'is-ok' : 'is-ng'}`}>
                <span>{paymentSummary?.isRegistered ? '決済登録完了' : '決済未登録'}</span>
                <strong>{getPaymentMethodCount(paymentSummary)} 件</strong>
              </div>
              <dl className="clinic-detail-definition">
                <div>
                  <dt>登録フラグ</dt>
                  <dd>{paymentSummary?.hasPaymentInfoFlag ? 'あり' : 'なし'}</dd>
                </div>
                <div>
                  <dt>Stripeモード</dt>
                  <dd>{paymentSummary?.stripeMode || '不明'}</dd>
                </div>
                <div>
                  <dt>支払い方法</dt>
                  <dd>{formatPaymentMethod(paymentSummary?.paymentMethod)}</dd>
                </div>
                <div>
                  <dt>Stripe Customer</dt>
                  <dd>
                    {paymentSummary?.stripeCustomerId ? (
                      <code className="clinic-detail-code">{paymentSummary.stripeCustomerId}</code>
                    ) : '未作成'}
                  </dd>
                </div>
                <div>
                  <dt>既定支払い方法</dt>
                  <dd>{paymentSummary?.defaultPaymentMethodLabel || '未設定'}</dd>
                </div>
              </dl>
              {getPaymentMethods(paymentSummary).length > 0 && (
                <div className="payment-card-list">
                  {getPaymentMethods(paymentSummary).map((method) => (
                    <div key={method.id} className="payment-card-item">
                      <div>
                        <strong>{formatPaymentMethodTitle(method)}</strong>
                        <span>{formatPaymentMethodSubText(method)}</span>
                      </div>
                      {method.isDefault && <span className="badge info">既定</span>}
                    </div>
                  ))}
                </div>
              )}
              {paymentSummary?.error && (
                <div className="clinic-detail-alert warning">{paymentSummary.error}</div>
              )}

              <div className="payment-history-block">
                <div className="payment-history-header">
                  <h3>決済履歴</h3>
                  <span>Stripe Invoice（新しい順）</span>
                </div>
                <p className="payment-history-note">
                  税列は Stripe 上の実徴収額です。売上一覧の税抜・税・税込も Stripe の税分離をそのまま反映し、未徴収の請求は税=0（推計なし）です。会計レポートの詳細は Stripe明細の売上一覧を参照してください。
                </p>
                {paymentHistoryLoading && !paymentHistory ? (
                  <div className="clinic-detail-empty">決済履歴を取得しています...</div>
                ) : paymentHistoryError ? (
                  <div className="clinic-detail-alert warning">{paymentHistoryError}</div>
                ) : paymentHistory?.error ? (
                  <div className="clinic-detail-alert warning">{paymentHistory.error}</div>
                ) : !(paymentHistory?.items || []).length ? (
                  <div className="clinic-detail-empty">決済履歴はありません</div>
                ) : (
                  <div className="payment-history-table-wrap">
                    <table className="payment-history-table">
                      <thead>
                        <tr>
                          <th>日時</th>
                          <th>状態</th>
                          <th>金額</th>
                          <th>税</th>
                          <th>Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(paymentHistory.items || []).map((item) => (
                          <tr key={item.id}>
                            <td>{formatUnixDateTime(item.paidAt || item.created)}</td>
                            <td>
                              <span className={`badge ${item.status === 'paid' ? 'success' : 'muted'}`}>
                                {formatInvoiceStatus(item.status)}
                              </span>
                            </td>
                            <td>{formatYenAmount(item.total)}</td>
                            <td>
                              {item.tax == null || item.tax === 0
                                ? '—'
                                : formatYenAmount(item.tax)}
                            </td>
                            <td>
                              {item.hostedInvoiceUrl ? (
                                <a
                                  href={item.hostedInvoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="payment-history-link"
                                >
                                  {item.number || item.id}
                                </a>
                              ) : (
                                <code className="clinic-detail-code">{item.number || item.id}</code>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      <ClinicStatisticsLogsPanel
        clinicId={effectiveClinic.id}
        clinicName={effectiveClinic.name}
      />

      <div className="clinic-detail-users">
        <AdminUserList
          users={users}
          clinics={clinicsForUserList}
          clinicStats={clinicStats}
          onUpdate={onUpdate}
          lockedClinicId={clinic.id}
          embedded
          serverPagination
          user={user}
        />
      </div>
    </div>
  );
};

export default AdminClinicDetail;
